<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import brandingAPI from 'dashboard/api/nexceo/branding';
import { useNexceoBranding } from 'dashboard/composables/useNexceoBranding';
import SectionLayout from './SectionLayout.vue';

const { isAdmin } = useAdmin();
const { refreshBranding, invalidate } = useNexceoBranding();

const loading = ref(true);
const saving = ref(false);
const uploadingType = ref(null);

const text = reactive({
  brand_name: '',
  brand_tagline: '',
  primary_color: '',
  pwa_name: '',
  pwa_short_name: '',
});

const assets = reactive({
  main_logo: null,
  sidebar_logo: null,
  sidebar_icon: null,
  login_logo: null,
  favicon: null,
  email_logo: null,
  pwa_icon_192: null,
  pwa_icon_512: null,
});

// Tracks which square-required assets are non-square after upload
const squareWarnings = reactive({});

// Asset types that must be square (shown in the square icons section)
const SQUARE_REQUIRED = new Set([
  'favicon',
  'sidebar_icon',
  'pwa_icon_192',
  'pwa_icon_512',
]);

const WIDE_LOGO_DEFS = [
  {
    key: 'main_logo',
    label: 'Main Logo',
    hint: 'Dashboard header and reports — recommended 200×50 px',
    accept: 'image/png,image/jpeg,image/jpg,image/svg+xml,image/webp',
  },
  {
    key: 'sidebar_logo',
    label: 'Sidebar Logo',
    hint: 'Left sidebar expanded state — recommended 140×32 px',
    accept: 'image/png,image/jpeg,image/jpg,image/svg+xml,image/webp',
  },
  {
    key: 'login_logo',
    label: 'Login Page Logo',
    hint: 'Shown on the login screen — recommended 200×50 px',
    accept: 'image/png,image/jpeg,image/jpg,image/svg+xml,image/webp',
  },
  {
    key: 'email_logo',
    label: 'Email / Report Logo',
    hint: 'Used in email headers and reports — recommended 200×50 px',
    accept: 'image/png,image/jpeg,image/jpg,image/svg+xml,image/webp',
  },
];

const SQUARE_ICON_DEFS = [
  {
    key: 'sidebar_icon',
    label: 'Collapsed Sidebar Icon',
    hint: 'Compact sidebar icon — must be square, recommended 32×32 px',
    accept: 'image/png,image/svg+xml,image/webp,image/x-icon',
    previewSize: 'sm',
  },
  {
    key: 'favicon',
    label: 'Favicon',
    hint: 'Browser tab icon — must be square, recommended 32×32 or 64×64 px, PNG/ICO',
    accept: 'image/png,image/x-icon,image/vnd.microsoft.icon,image/svg+xml',
    previewSize: 'sm',
  },
  {
    key: 'pwa_icon_192',
    label: 'App Icon 192×192',
    hint: 'Android home screen & notification icon — must be 192×192 px square PNG',
    accept: 'image/png,image/webp',
    previewSize: 'lg',
  },
  {
    key: 'pwa_icon_512',
    label: 'App Icon 512×512',
    hint: 'Windows/desktop taskbar & PWA splash — must be 512×512 px square PNG',
    accept: 'image/png,image/webp',
    previewSize: 'lg',
  },
];

// Preview: which icon src to show for browser tab and app icon
const previewFaviconSrc = computed(
  () => assets.favicon || '/favicon-32x32.png'
);
const previewAppIconSrc = computed(
  () =>
    assets.pwa_icon_192 || assets.pwa_icon_512 || '/brand/nexceo-icon-192.png'
);
const previewBrandName = computed(
  () => text.pwa_short_name || text.brand_name || 'NEXCEO'
);

function installPromptStatus() {
  if (window.nexceoPwaInstalled) return 'Installed';
  if (window.nexceoPwaPrompt) return 'Available';
  return 'Not yet / iOS';
}

const pwaStatus = computed(() => [
  {
    label: 'Manifest',
    ok: !!document.getElementById('pwa-manifest'),
    value: document.getElementById('pwa-manifest') ? 'Linked' : 'Missing',
  },
  {
    label: 'Service Worker',
    ok: 'serviceWorker' in navigator,
    value: 'serviceWorker' in navigator ? 'Supported' : 'Unsupported',
  },
  {
    label: 'Install Prompt',
    ok: !!window.nexceoPwaPrompt,
    value: installPromptStatus(),
  },
  {
    label: 'Favicon',
    ok: !!assets.favicon,
    value: assets.favicon ? 'Uploaded' : 'Default',
  },
  {
    label: 'App Icon 192',
    ok: !!assets.pwa_icon_192,
    value: assets.pwa_icon_192 ? 'Uploaded' : 'Using favicon / fallback',
  },
  {
    label: 'App Icon 512',
    ok: !!assets.pwa_icon_512,
    value: assets.pwa_icon_512 ? 'Uploaded' : 'Using fallback',
  },
]);

// Returns a promise resolving to true if the image file is square
function checkSquare(file) {
  return new Promise(resolve => {
    if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') {
      resolve(true); // SVG: skip check
      return;
    }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img.width === img.height);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(true); // don't block upload on read error
    };
    img.src = url;
  });
}

async function fetchBranding() {
  try {
    const { data } = await brandingAPI.get();
    text.brand_name = data.brand_name || '';
    text.brand_tagline = data.brand_tagline || '';
    text.primary_color = data.primary_color || '';
    text.pwa_name = data.pwa_name || '';
    text.pwa_short_name = data.pwa_short_name || '';
    Object.assign(assets, data.assets || {});
  } catch {
    // ignore
  } finally {
    loading.value = false;
  }
}

async function saveText() {
  if (!isAdmin.value) return;
  saving.value = true;
  try {
    const { data } = await brandingAPI.update({
      nexceo_brand_name: text.brand_name,
      nexceo_brand_tagline: text.brand_tagline,
      nexceo_primary_color: text.primary_color,
      nexceo_pwa_name: text.pwa_name,
      nexceo_pwa_short_name: text.pwa_short_name,
    });
    refreshBranding(data);
    useAlert('Brand identity saved.');
  } catch {
    useAlert('Failed to save. Please try again.');
  } finally {
    saving.value = false;
  }
}

function triggerUpload(assetType) {
  document.getElementById(`nexceo-upload-${assetType}`)?.click();
}

async function onFileSelected(assetType, event) {
  const file = event.target.files?.[0];
  if (!file) return;

  if (file.size > 2 * 1024 * 1024) {
    useAlert('File too large. Maximum size is 2 MB.');
    return;
  }

  // Square check for favicon and PWA icons — warn but don't block
  if (SQUARE_REQUIRED.has(assetType)) {
    const isSquare = await checkSquare(file);
    squareWarnings[assetType] = !isSquare;
    if (!isSquare) {
      useAlert(
        `Warning: this image is not square. ${assetType === 'favicon' ? 'Favicon' : 'PWA icons'} should be square for best results.`
      );
    }
  }

  uploadingType.value = assetType;
  try {
    const { data } = await brandingAPI.upload(assetType, file);
    assets[assetType] = data.url;
    invalidate();
    refreshBranding({ ...text, assets: { ...assets } });
    useAlert('Logo uploaded successfully.');
  } catch (err) {
    useAlert(err?.response?.data?.error || 'Upload failed. Please try again.');
  } finally {
    uploadingType.value = null;
    event.target.value = '';
  }
}

async function removeAsset(assetType) {
  try {
    await brandingAPI.removeAsset(assetType);
    assets[assetType] = null;
    delete squareWarnings[assetType];
    invalidate();
    refreshBranding({ ...text, assets: { ...assets } });
    useAlert('Logo removed.');
  } catch {
    useAlert('Failed to remove logo.');
  }
}

onMounted(fetchBranding);
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/first-attribute-linebreak, vue/max-attributes-per-line -->
<template>
  <SectionLayout
    title="Branding"
    description="Upload your company logos and set your brand identity. Changes apply to the sidebar, browser tab, and reports."
    with-border
  >
    <div v-if="loading" class="text-sm text-n-slate-9 py-4">
      Loading branding settings…
    </div>

    <template v-else>
      <!-- Brand Identity -->
      <div class="mb-8">
        <h5 class="text-sm font-semibold text-n-slate-12 mb-4">
          Brand Identity
        </h5>
        <div class="grid gap-4 max-w-lg">
          <div>
            <label class="block text-xs font-medium text-n-slate-11 mb-1">
              Brand Name
            </label>
            <input
              v-model="text.brand_name"
              type="text"
              class="w-full rounded-md border border-n-weak px-3 py-2 text-sm text-n-slate-12 bg-white focus:outline-none focus:ring-1 focus:ring-woot-500"
              placeholder="NEXCEO"
              :disabled="!isAdmin"
            />
          </div>
          <div>
            <label class="block text-xs font-medium text-n-slate-11 mb-1">
              Tagline
            </label>
            <input
              v-model="text.brand_tagline"
              type="text"
              class="w-full rounded-md border border-n-weak px-3 py-2 text-sm text-n-slate-12 bg-white focus:outline-none focus:ring-1 focus:ring-woot-500"
              placeholder="Real Estate AI Workforce"
              :disabled="!isAdmin"
            />
          </div>
          <div>
            <label class="block text-xs font-medium text-n-slate-11 mb-1">
              Primary Color
            </label>
            <div class="flex items-center gap-3">
              <input
                v-model="text.primary_color"
                type="color"
                class="h-9 w-14 rounded border border-n-weak cursor-pointer"
                :disabled="!isAdmin"
              />
              <input
                v-model="text.primary_color"
                type="text"
                class="w-32 rounded-md border border-n-weak px-3 py-2 text-sm text-n-slate-12 bg-white focus:outline-none focus:ring-1 focus:ring-woot-500"
                placeholder="#2481F4"
                :disabled="!isAdmin"
              />
            </div>
          </div>
          <!-- PWA App Name -->
          <div class="border-t border-n-weak pt-4 mt-2">
            <p
              class="text-xs font-semibold text-n-slate-10 uppercase tracking-wide mb-3"
            >
              PWA / App Name
            </p>
            <div class="grid gap-3">
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">
                  App Name (full)
                </label>
                <input
                  v-model="text.pwa_name"
                  type="text"
                  class="w-full rounded-md border border-n-weak px-3 py-2 text-sm text-n-slate-12 bg-white focus:outline-none focus:ring-1 focus:ring-woot-500"
                  placeholder="NEXCEO CRM"
                  :disabled="!isAdmin"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">
                  Short App Name
                </label>
                <input
                  v-model="text.pwa_short_name"
                  type="text"
                  class="w-full rounded-md border border-n-weak px-3 py-2 text-sm text-n-slate-12 bg-white focus:outline-none focus:ring-1 focus:ring-woot-500"
                  placeholder="NEXCEO"
                  maxlength="12"
                  :disabled="!isAdmin"
                />
                <p class="text-xs text-n-slate-8 mt-1">
                  Max 12 characters — shown under the home screen icon.
                </p>
              </div>
            </div>
          </div>

          <div v-if="isAdmin">
            <button
              class="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-woot-500 hover:bg-woot-600 text-white text-sm font-medium transition-colors disabled:opacity-60"
              :disabled="saving"
              @click="saveText"
            >
              <span
                v-if="saving"
                class="i-lucide-loader-2 size-3.5 animate-spin"
              />
              {{ saving ? 'Saving…' : 'Save Brand Identity' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Wide Logos -->
      <div class="mb-8">
        <h5 class="text-sm font-semibold text-n-slate-12 mb-1">Wide Logos</h5>
        <p class="text-xs text-n-slate-9 mb-4">
          Horizontal logos used in the UI. Not used as app/taskbar icons.
        </p>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            v-for="def in WIDE_LOGO_DEFS"
            :key="def.key"
            class="border border-n-weak rounded-lg p-4 bg-white"
          >
            <div class="flex items-start justify-between mb-2">
              <div>
                <p class="text-sm font-medium text-n-slate-12 leading-tight">
                  {{ def.label }}
                </p>
                <p class="text-xs text-n-slate-9 mt-0.5">{{ def.hint }}</p>
              </div>
              <span
                v-if="assets[def.key]"
                class="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5 shrink-0 ml-2"
              >
                <span class="i-lucide-check size-3" />
                Uploaded
              </span>
            </div>
            <div
              class="mb-3 h-16 flex items-center justify-center rounded bg-n-bg border border-dashed border-n-weak overflow-hidden"
            >
              <img
                v-if="assets[def.key]"
                :src="assets[def.key]"
                :alt="def.label"
                class="max-h-14 max-w-full object-contain"
              />
              <span v-else class="text-xs text-n-slate-8"
                >No logo uploaded</span
              >
            </div>
            <input
              :id="`nexceo-upload-${def.key}`"
              type="file"
              class="hidden"
              :accept="def.accept"
              :disabled="!isAdmin"
              @change="onFileSelected(def.key, $event)"
            />
            <div v-if="isAdmin" class="flex gap-2">
              <button
                class="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded border border-n-weak text-xs text-n-slate-11 hover:bg-n-bg transition-colors disabled:opacity-60"
                :disabled="uploadingType === def.key"
                @click="triggerUpload(def.key)"
              >
                <span
                  v-if="uploadingType === def.key"
                  class="i-lucide-loader-2 size-3 animate-spin"
                />
                <span v-else class="i-lucide-upload size-3" />
                {{ uploadingType === def.key ? 'Uploading…' : 'Upload' }}
              </button>
              <button
                v-if="assets[def.key]"
                class="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-red-200 text-xs text-red-600 hover:bg-red-50 transition-colors"
                @click="removeAsset(def.key)"
              >
                <span class="i-lucide-trash-2 size-3" />
                Remove
              </button>
            </div>
            <p v-else class="text-xs text-n-slate-8 mt-1">
              Admin access required to edit.
            </p>
          </div>
        </div>
      </div>

      <!-- Square Icons / PWA -->
      <div class="mb-8">
        <h5 class="text-sm font-semibold text-n-slate-12 mb-1">
          Square Icons &amp; PWA Icons
        </h5>
        <p class="text-xs text-n-slate-9 mb-4">
          Must be square. Used for browser tab, home screen, and Windows/desktop
          taskbar.
        </p>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            v-for="def in SQUARE_ICON_DEFS"
            :key="def.key"
            class="border rounded-lg p-4 bg-white"
            :class="
              squareWarnings[def.key] ? 'border-amber-300' : 'border-n-weak'
            "
          >
            <!-- Header -->
            <div class="flex items-start justify-between mb-2">
              <div>
                <p class="text-sm font-medium text-n-slate-12 leading-tight">
                  {{ def.label }}
                </p>
                <p class="text-xs text-n-slate-9 mt-0.5">{{ def.hint }}</p>
              </div>
              <span
                v-if="assets[def.key]"
                class="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5 shrink-0 ml-2"
              >
                <span class="i-lucide-check size-3" />
                Uploaded
              </span>
            </div>

            <!-- Non-square warning -->
            <div
              v-if="squareWarnings[def.key]"
              class="mb-2 flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5"
            >
              <span class="i-lucide-triangle-alert size-3.5 shrink-0" />
              Image is not square — it may appear distorted as an app/taskbar
              icon. Upload a square PNG for best results.
            </div>

            <!-- Preview box — square for icons -->
            <div
              class="mb-3 flex items-center justify-center rounded bg-n-bg border border-dashed border-n-weak overflow-hidden"
              :class="def.previewSize === 'lg' ? 'h-24 w-24' : 'h-16 w-16'"
            >
              <img
                v-if="assets[def.key]"
                :src="assets[def.key]"
                :alt="def.label"
                class="max-h-full max-w-full object-contain"
              />
              <span v-else class="text-[10px] text-n-slate-8 text-center px-1"
                >No icon</span
              >
            </div>

            <input
              :id="`nexceo-upload-${def.key}`"
              type="file"
              class="hidden"
              :accept="def.accept"
              :disabled="!isAdmin"
              @change="onFileSelected(def.key, $event)"
            />

            <div v-if="isAdmin" class="flex gap-2">
              <button
                class="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded border border-n-weak text-xs text-n-slate-11 hover:bg-n-bg transition-colors disabled:opacity-60"
                :disabled="uploadingType === def.key"
                @click="triggerUpload(def.key)"
              >
                <span
                  v-if="uploadingType === def.key"
                  class="i-lucide-loader-2 size-3 animate-spin"
                />
                <span v-else class="i-lucide-upload size-3" />
                {{ uploadingType === def.key ? 'Uploading…' : 'Upload' }}
              </button>
              <button
                v-if="assets[def.key]"
                class="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-red-200 text-xs text-red-600 hover:bg-red-50 transition-colors"
                @click="removeAsset(def.key)"
              >
                <span class="i-lucide-trash-2 size-3" />
                Remove
              </button>
            </div>
            <p v-else class="text-xs text-n-slate-8 mt-1">
              Admin access required to edit.
            </p>
          </div>
        </div>
      </div>

      <!-- Preview panel -->
      <div class="mb-8">
        <h5 class="text-sm font-semibold text-n-slate-12 mb-3">Preview</h5>
        <div class="flex flex-wrap gap-6">
          <!-- Browser tab preview -->
          <div>
            <p class="text-xs text-n-slate-9 mb-2">Browser Tab</p>
            <div
              class="inline-flex items-center gap-2 bg-white border border-n-weak rounded-t-md px-3 py-1.5 shadow-sm"
            >
              <img
                :src="previewFaviconSrc"
                alt="Favicon"
                class="size-4 object-contain rounded-sm"
                @error="$event.target.style.display = 'none'"
              />
              <span class="text-xs text-n-slate-12 max-w-[120px] truncate">{{
                previewBrandName
              }}</span>
            </div>
          </div>

          <!-- App icon preview -->
          <div>
            <p class="text-xs text-n-slate-9 mb-2">
              Home Screen / Taskbar Icon
            </p>
            <div class="flex flex-col items-center gap-1">
              <div
                class="size-16 rounded-xl bg-n-bg border border-dashed border-n-weak overflow-hidden flex items-center justify-center"
              >
                <img
                  :src="previewAppIconSrc"
                  alt="App icon"
                  class="size-full object-cover"
                  @error="$event.target.style.display = 'none'"
                />
              </div>
              <span class="text-[10px] text-n-slate-9">{{
                previewBrandName
              }}</span>
            </div>
          </div>

          <!-- Windows taskbar preview -->
          <div>
            <p class="text-xs text-n-slate-9 mb-2">Windows Taskbar</p>
            <div
              class="inline-flex items-center gap-1.5 bg-slate-800 text-white rounded px-2 py-1.5"
            >
              <div class="size-5 rounded overflow-hidden bg-slate-600">
                <img
                  :src="previewAppIconSrc"
                  alt="Taskbar icon"
                  class="size-full object-cover"
                  @error="$event.target.style.display = 'none'"
                />
              </div>
              <span class="text-[10px]">{{ previewBrandName }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- PWA Status -->
      <div class="mb-8">
        <h5 class="text-sm font-semibold text-n-slate-12 mb-3">PWA Status</h5>
        <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div
            v-for="item in pwaStatus"
            :key="item.label"
            class="rounded-lg border px-3 py-2"
            :class="
              item.ok
                ? 'border-green-200 bg-green-50'
                : 'border-amber-200 bg-amber-50'
            "
          >
            <p
              class="text-xs font-medium"
              :class="item.ok ? 'text-green-700' : 'text-amber-700'"
            >
              {{ item.label }}
            </p>
            <p
              class="text-xs mt-0.5"
              :class="item.ok ? 'text-green-600' : 'text-amber-600'"
            >
              {{ item.value }}
            </p>
          </div>
        </div>
      </div>

      <!-- Notes -->
      <div
        class="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-800 space-y-1"
      >
        <p>
          <strong>Favicon</strong> — updates in the browser tab after page
          refresh.
        </p>
        <p>
          <strong>App / taskbar icon</strong> — Chrome and Windows cache
          installed PWA icons separately from normal HTTP cache. After changing
          icons, <em>remove the installed app and reinstall it</em> from the
          browser to see the new icon. A browser refresh alone is not enough.
        </p>
        <p>
          <strong>Manifest</strong> — icon URLs include a version timestamp so
          Chrome downloads fresh icons when the manifest is re-read. However,
          the installed icon cache is only cleared on reinstall.
        </p>
      </div>
    </template>
  </SectionLayout>
</template>
